import { AxiosResponse } from 'axios';
import ClientType from 'core/classes/ClientType';
import IWebClient from 'core/types/IWebClient';
import DoorAccess from 'smartHome/larva/classes/DoorAccess';
import DoorAccessType from 'smartHome/larva/classes/DoorAccessType';
import DoorPinCode from 'smartHome/larva/classes/DoorPinCode';
import IDoorCardRawData from 'smartHome/larva/types/doorAccess/IDoorCardRawData';
import IDoorCodeRawData from 'smartHome/larva/types/doorAccess/IDoorCodeRawData';
import ILarvaDoorAccess from 'smartHome/larva/types/doorAccess/ILarvaDoorAccess';
import ILarvaSelectedDoorAccess from 'smartHome/larva/types/doorAccess/ILarvaSelectedDoorAccess';
import LarvaPaginatedResponse from 'smartHome/larva/types/LarvaPaginatedResponse';
import LarvaConnectionConfig from './LarvaConnectionConfig';
import LarvaWebClient from './LarvaWebClient';

class LarvaDoorAccessController {
    private _client?: IWebClient;
    private static _instance: LarvaDoorAccessController;

    public static getInstance(webClient?: IWebClient): LarvaDoorAccessController {
        if (!LarvaDoorAccessController._instance) {
            if (!webClient) {
                throw new Error('WebClient is required for the first instantiation');
            }
            LarvaDoorAccessController._instance = new LarvaDoorAccessController(webClient);
        }
        return LarvaDoorAccessController._instance;
    }

    constructor(webClient: IWebClient) {
        this.client = webClient;
    }

    public static generateFromConnectionConfig(config: LarvaConnectionConfig): LarvaDoorAccessController {
        return LarvaDoorAccessController.getInstance(new LarvaWebClient(config));
    }

    get client(): IWebClient {
        if (this._client) {
            return this._client;
        }

        throw new ReferenceError('Undefined web client');
    }

    set client(value: unknown) {
        if (
            value &&
            typeof value === 'object' &&
            'type' in value &&
            (value as IWebClient).type === ClientType.WEB_CLIENT
        ) {
            this._client = value as IWebClient;
            return;
        }

        throw new TypeError('Invalid web client');
    }

    createCard(access: unknown): Promise<AxiosResponse> {
        if (access instanceof DoorAccess) {
            return this.client.post('cards', access.cardData) as Promise<AxiosResponse>;
        }

        throw new TypeError('Invalid card');
    }

    createCode(access: unknown): Promise<AxiosResponse> {
        if (access instanceof DoorAccess) {
            // Ugly fix, because backend now throws error on optional id field.
            const codeData = { ...access.codeData };
            // @ts-ignore
            delete codeData.id;

            return this.client.post('users', codeData) as Promise<AxiosResponse>;
        }

        throw new TypeError('Invalid code access');
    }

    async getCodes() {
        const response = (await this.client.get('users')) as Promise<AxiosResponse<LarvaPaginatedResponse>>;
        // @ts-ignore
        return response?.data?.data || [];
    }

    async getCards() {
        const response = (await this.client.get('cards')) as Promise<AxiosResponse<LarvaPaginatedResponse>>;
        //@ts-ignore
        return response?.data?.data || [];
    }

    async updateAccessPin(
        access: ILarvaSelectedDoorAccess,
    ): Promise<AxiosResponse<IDoorCodeRawData> | AxiosResponse<IDoorCardRawData> | null> {
        switch (access.accessType) {
            case DoorAccessType.CODE: {
                return (await this.client.post(`users/${access.id}/pin`, {
                    pin: DoorPinCode.GENERATE,
                })) as AxiosResponse<IDoorCodeRawData>;
            }
            case DoorAccessType.CARD: {
                return (await this.client.patch(`cards/${access.id}`, {
                    pin: DoorPinCode.GENERATE,
                })) as AxiosResponse<IDoorCardRawData>;
            }
            default: {
                return null;
            }
        }
    }

    async unsetAccessPin(
        access: ILarvaSelectedDoorAccess,
    ): Promise<AxiosResponse<IDoorCodeRawData> | AxiosResponse<IDoorCardRawData> | null> {
        switch (access.accessType) {
            case DoorAccessType.CODE: {
                return (await this.client.post(`users/${access.id}/pin`, {
                    pin: DoorPinCode.UNSET,
                })) as AxiosResponse<IDoorCodeRawData>;
            }
            case DoorAccessType.CARD: {
                return (await this.client.patch(`cards/${access.id}`, {
                    pin: DoorPinCode.UNSET,
                })) as AxiosResponse<IDoorCardRawData>;
            }
            default: {
                return null;
            }
        }
    }

    async createAccess(access: DoorAccess): Promise<AxiosResponse> {
        if (DoorAccessType.isCode(access.accessType)) {
            return this.createCode(access);
        }

        return this.createCard(access);
    }

    async deleteCard(access: DoorAccess): Promise<AxiosResponse> {
        return (await this.client.delete(`cards/${access.id}`)) as Promise<AxiosResponse>;
    }

    async deleteCode(access: DoorAccess): Promise<AxiosResponse> {
        return (await this.client.delete(`users/${access.id}`)) as Promise<AxiosResponse>;
    }

    async deleteAccess(access: DoorAccess): Promise<AxiosResponse> {
        if (DoorAccessType.isCode(access.accessType)) {
            return this.deleteCode(access);
        }

        return this.deleteCard(access);
    }

    async sendPinCodeBySMS(access: DoorAccess): Promise<AxiosResponse | null> {
        if (DoorAccessType.isCode(access.accessType)) {
            const url = `users/${access.id}/send-sms`;
            return this.client.post(url, { phone: access.phone }) as Promise<AxiosResponse>;
        }

        return null;
    }

    async sendPinCodeByEmail(access: DoorAccess): Promise<AxiosResponse | null> {
        if (DoorAccessType.isCode(access.accessType)) {
            const url = `users/${access.id}/send-email`;
            return this.client.post(url, { email: access.email }) as Promise<AxiosResponse>;
        }

        return null;
    }

    async updateAccessData(
        access: ILarvaDoorAccess,
        data: { [field: string]: string },
    ): Promise<AxiosResponse<IDoorCodeRawData>> {
        const url = access.accessType === DoorAccessType.CODE ? `users/${access.id}` : `cards/${access.id}`;

        return (await this.client.patch(url, data)) as AxiosResponse<IDoorCodeRawData>;
    }
}

export default LarvaDoorAccessController;
